import React from 'react';
import { STATUS_LABEL } from '../constants';
import { IStepPanelProps } from '../types';
import PosthogAnalyticsCard from './PosthogAnalyticsCard';
import BranchDynamicLinkCard from './branch/BranchDynamicLinkCard';
import OneSignalProvisionCard from './onesignal/OneSignalProvisionCard';
import MobileAppStatusPanel from './mobileApp/MobileAppStatusPanel';

const StepPanel: React.FC<IStepPanelProps> = ({ step, communityId, customDomain, branchDetails, posthogApiKey, onRefetch }) => (
	<div className={`card np-step-panel ${step.status} mb-0`}>
		<div className="card-header d-flex align-items-center" style={{ background: '#f8f9fa' }}>
			<i className={`${step.icon} mr-2 text-primary`}></i>
			<h5 className="mb-0 mr-3">{step.label}</h5>
			<span className={`np-status-badge ${step.status}`}>{STATUS_LABEL[step.status]}</span>
		</div>
		<div className="card-body">
			{step.status === 'blocked' && (
				<div className="alert alert-warning py-2 mb-3">
					<i className="fas fa-lock mr-1"></i>
					<strong>Locked.</strong> This step becomes available once the <em>Custom Domain</em> step is marked complete.
				</div>
			)}

			<p className="mb-3">{step.description}</p>

			{/* Custom domain hostname read-out */}
			{step.id === 'custom_domain' && (
				<div className="mb-3 d-flex align-items-center">
					<span className="text-muted small mr-2">Domain:</span>
					{customDomain ? (
						<code className="np-os-appid-input px-2 py-1 rounded" style={{ fontSize: '0.82rem', background: '#f8f9fa' }}>
							{customDomain}
						</code>
					) : (
						<span className="text-muted small">— not configured yet</span>
					)}
				</div>
			)}

			<ul className="list-group list-group-flush mb-3">
				{step.details.map((detail, i) => (
					<li key={i} className="list-group-item px-0 py-2" style={{ background: 'transparent' }}>
						<i className="fas fa-angle-right text-muted mr-2"></i>
						{detail}
					</li>
				))}
			</ul>

			{/* Branch-specific action */}
			{step.id === 'branch' && step.status !== 'blocked' && (
				<BranchDynamicLinkCard
					communityId={communityId}
					branchDetails={branchDetails}
					onRefetch={onRefetch}
				/>
			)}

			{/* Branded mobile app — the only place a record can be advanced. */}
			{step.id === 'mobile_app' && (
				<MobileAppStatusPanel communityId={communityId} />
			)}

			{/* OneSignal-specific action */}
			{step.id === 'onesignal' && step.status !== 'blocked' && (
				<OneSignalProvisionCard
					communityId={communityId}
					isCompleted={step.status === 'completed'}
					onRefetch={onRefetch}
				/>
			)}

			{/* PostHog Analytics-specific action */}
			{step.id === 'posthog_analytics' && step.status !== 'blocked' && (
				<PosthogAnalyticsCard
					communityId={communityId}
					posthogApiKey={posthogApiKey}
					onRefetch={onRefetch}
				/>
			)}

			{/* Generic placeholder for steps without a dedicated action yet */}
			{step.id !== 'onesignal' && step.id !== 'branch' && step.id !== 'posthog_analytics' && step.id !== 'mobile_app' && step.status !== 'blocked' && step.status !== 'completed' && (
				<div className="alert alert-info py-2 mb-0">
					<i className="fas fa-info-circle mr-1"></i>
					Configuration for this step will be wired in the next phase.
				</div>
			)}

			{step.status === 'completed' && step.id !== 'onesignal' && step.id !== 'branch' && step.id !== 'posthog_analytics' && step.id !== 'mobile_app' && (
				<div className="alert alert-success py-2 mb-0">
					<i className="fas fa-check-circle mr-1"></i>
					This step has been completed and locked.
				</div>
			)}
		</div>
	</div>
);

export default StepPanel;
